/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description claim request fields mapping
 */

import _ from 'lodash';
import { formatCurrency } from 'utils/admin/payments';
import {
	CLAIM_RELATED_CAUSES_CODES,
	payerResponsibilitySequenceMapping,
	PLAN_ASSIGNMENT_CODES,
	SSN_AND_EIN_CODES,
	YES_NO_CODES,
} from 'app-constants/claims/claimRequest';

// get telephone from contact points
const getTelephone = (contactPoints: InstitutionalClaimParser.ContactPointMeta[]): string => {
	const contactPoint = _.find(contactPoints, (item) => _.some(item?.telecom, (t) => t?.system === 'phone'));

	const phoneContact = _.find(contactPoint?.telecom, (t) => t?.system === 'phone');

	return phoneContact?.value || '';
};

// get display or code from coding
const getDisplayOrCode = (coding?: Fhir.Coding): string => {
	return coding?.display || coding?.code || '';
};

// get quantity with unit from quantity
const getQuantity = (quantity?: Fhir.Quantity): string => {
	if (!quantity) {
		return '';
	}
	return `${quantity?.value || ''} ${quantity?.unit || quantity?.code || ''}`;
};

// get pay to group meta from pay to address
const getPayToGroupMeta = (payToPlan?: InstitutionalClaimParser.PayToPlan): AnyType => {
	return {
		groupName: payToPlan?.name,
		firstName: payToPlan?.contact?.[0]?.name?.[0]?.firstName || '',
		lastName: payToPlan?.contact?.[0]?.name?.[0]?.lastName || '',
		middleName: _.compact(payToPlan?.contact?.[0]?.name?.[0]?.prefix).join(' ') || '',
		addressLine1: payToPlan?.contact?.[0]?.address?.line?.[0],
		addressLine2: payToPlan?.contact?.[0]?.address?.line?.[1],
		city: payToPlan?.contact?.[0]?.address?.city,
		state: payToPlan?.contact?.[0]?.address?.state,
		zip: payToPlan?.contact?.[0]?.address?.postalCode,
		telephone: getTelephone(payToPlan?.contact),
		payToTaxID: payToPlan?.payorIdentification?.value,
	};
};

// get first care team meta from care team meta
const getFirstCareTeamMeta = (careTeamMeta: Record<number, InstitutionalClaimParser.ICareTeam>): InstitutionalClaimParser.ICareTeam => {
	return _.first(_.values(careTeamMeta));
};

// get secondary id from care team meta
const getSecondaryId = (
	careTeam: InstitutionalClaimParser.ICareTeam,
	excludeNpi?: boolean,
): InstitutionalClaimParser.IdentifierResponse => {
	const secondaryId =
		careTeam?.providerCommercialNumber || careTeam?.providerUPINNumber || careTeam?.stateLicenseNumber || careTeam?.locationNumber;
	if (excludeNpi) {
		return secondaryId;
	}
	return secondaryId || careTeam?.npi;
};

// format secondary id from care team meta
const formatSecondaryId = (secondaryId: InstitutionalClaimParser.IdentifierResponse): string => {
	if (!secondaryId) {
		return '';
	}
	return `${secondaryId?.display} - ${secondaryId?.value}`;
};

// get physician type code based on care team presence
const getPhysicianTypeCode = (
	primaryTeam: Record<number, InstitutionalClaimParser.ICareTeam>,
	primaryCode: string,
	referringProvider: Record<number, InstitutionalClaimParser.ICareTeam>,
): string => {
	if (!_.isEmpty(primaryTeam)) {
		return primaryCode;
	}
	if (!_.isEmpty(referringProvider)) {
		return 'DN';
	}
	return null;
};

// get tax id type based on ein/ssn presence
const getTaxIdType = (ein: string, ssn: string): string => {
	if (ein) {
		return SSN_AND_EIN_CODES.ein;
	}
	if (ssn) {
		return SSN_AND_EIN_CODES.ssn;
	}
	return '';
};

// get accept assignment code based on assignment flags
const getAcceptAssignmentCode = (isAccepted: boolean, isNotAccepted: boolean): string => {
	if (isAccepted) {
		return YES_NO_CODES.Y;
	}
	if (isNotAccepted) {
		return YES_NO_CODES.N;
	}
	return '';
};

// get institutional claim initial model
const getInstitutionalClaimInitialModel = (response: InstitutionalClaimParser.IResponse): InstitutionalForm.FormModel => {
	const {
		payer,
		billingProvider,
		patient,
		claimInfo,
		eventsMeta,
		extensionMeta,
		subscribersAndPayers,
		careTeamMeta,
		supportingInfoMeta,
		diagnosisInfo,
		procedureInfo,
		serviceFacility,
	} = response;
	const payToGroupMeta = getPayToGroupMeta(extensionMeta?.payToPlan);
	const primarySubscriber = subscribersAndPayers?.[0]?.subscriber;
	// get other physician 1 and 2 from care team meta
	const otherPhysician1Source = _.isEmpty(careTeamMeta?.otherOperatingPhysician)
		? careTeamMeta?.referringProvider
		: careTeamMeta?.otherOperatingPhysician;
	const otherPhysician1 = getFirstCareTeamMeta(otherPhysician1Source);
	const otherPhysician1TypeCode = getPhysicianTypeCode(careTeamMeta?.otherOperatingPhysician, 'ZZ', careTeamMeta?.referringProvider);
	const otherPhysician1SecondaryId = getSecondaryId(otherPhysician1, true);

	const otherPhysician2Source = _.isEmpty(careTeamMeta?.renderingProvider)
		? careTeamMeta?.referringProvider
		: careTeamMeta?.renderingProvider;
	const otherPhysician2 = getFirstCareTeamMeta(otherPhysician2Source);
	const otherPhysician2TypeCode = getPhysicianTypeCode(careTeamMeta?.renderingProvider, '82', careTeamMeta?.referringProvider);
	const otherPhysician2SecondaryId = getSecondaryId(otherPhysician2, true);
	// get attending physician from care team meta
	const attendingPhysician = getFirstCareTeamMeta(careTeamMeta?.attendingPhysician);
	const attendingPhysicianSecondaryId = getSecondaryId(attendingPhysician, true);
	const operatingPhysician = getFirstCareTeamMeta(careTeamMeta?.operatingPhysician);
	const operatingPhysicianSecondaryId = getSecondaryId(operatingPhysician, true);
	const isSecondaryClaim = claimInfo?.payerResponsibilitySequence === payerResponsibilitySequenceMapping?.[2]?.order;

	return {
		// first row
		row1: {
			payer: {
				payerName: payer?.name,
				payerId: payer?.payorIdentification?.value,
				payerType: getDisplayOrCode(subscribersAndPayers?.[0]?.coverage?.payerTypeCoding),
				addressLine1: payer?.contact?.[0]?.address?.line?.[0],
				addressLine2: payer?.contact?.[0]?.address?.line?.[1],
				city: payer?.contact?.[0]?.address?.city,
				state: payer?.contact?.[0]?.address?.state,
				zip: payer?.contact?.[0]?.address?.postalCode,
			},
		},

		// second row
		row2: {
			// billing provider
			billingGroup: {
				billingGroupName: billingProvider?.name,
				firstName: billingProvider?.nameMeta?.[0]?.firstName,
				lastName: billingProvider?.nameMeta?.[0]?.lastName,
				middleName: billingProvider?.nameMeta?.[0]?.middleNames?.[0],
				addressLine1: billingProvider?.address?.[0]?.line?.[0] || billingProvider?.contact?.[0]?.address?.line?.[0],
				addressLine2: billingProvider?.address?.[0]?.line?.[1] || billingProvider?.contact?.[0]?.address?.line?.[1],
				city: billingProvider?.address?.[0]?.city || billingProvider?.contact?.[0]?.address?.city,
				state: billingProvider?.address?.[0]?.state || billingProvider?.contact?.[0]?.address?.state,
				zip: billingProvider?.address?.[0]?.postalCode || billingProvider?.contact?.[0]?.address?.postalCode,
				telephone: getTelephone(billingProvider?.contact),
				pin: '',
			},
			// pay to plan/ provider
			payToGroup: {
				payToGroupName: payToGroupMeta?.groupName,
				firstName: payToGroupMeta?.firstName,
				lastName: payToGroupMeta?.lastName,
				middleName: payToGroupMeta?.middleName,
				addressLine1: payToGroupMeta?.addressLine1,
				addressLine2: payToGroupMeta?.addressLine2,
				city: payToGroupMeta?.city,
				state: payToGroupMeta?.state,
				zip: payToGroupMeta?.zip,
				telephone: payToGroupMeta?.telephone,
				payToTaxID: payToGroupMeta?.payToTaxID,
			},
			// identifiers
			identification: {
				patientControlNumber: claimInfo?.patientControlNumber,
				medicalRecordNumber: patient?.medicalRecordIdentificationNumber?.value,
				typeOfBill: extensionMeta?.clmSection?.facilityTypeCode,
				federalTaxId: billingProvider?.ein?.value,
				fromDate: response?.billablePeriod?.start,
				toDate: response?.billablePeriod?.end,
			},
		},

		// third row
		row3: {
			// patient Name
			patientName: {
				patientID: primarySubscriber?.memberId?.value,
				firstName: patient?.nameMeta?.[0]?.firstName || primarySubscriber?.nameMeta?.[0]?.firstName,
				lastName: patient?.nameMeta?.[0]?.lastName || primarySubscriber?.nameMeta?.[0]?.lastName,
				middleName: patient?.nameMeta?.[0]?.middleNames?.[0] || primarySubscriber?.nameMeta?.[0]?.middleNames?.[0],
			},
			// patient address
			patientAddress: {
				addressLine1: patient?.address?.[0]?.line?.[0] || primarySubscriber?.address?.[0]?.line?.[0],
				addressLine2: patient?.address?.[0]?.line?.[1] || primarySubscriber?.address?.[0]?.line?.[1],
				city: patient?.address?.[0]?.city || primarySubscriber?.address?.[0]?.city,
				state: patient?.address?.[0]?.state || primarySubscriber?.address?.[0]?.state,
				zip: patient?.address?.[0]?.postalCode || primarySubscriber?.address?.[0]?.postalCode,
				countryCode: patient?.address?.[0]?.country || primarySubscriber?.address?.[0]?.country,
			},
			// dob and sex
			birthDate: {
				birthDate: patient?.birthDate || primarySubscriber?.birthDate,
			},
			gender: {
				gender: patient?.gender || primarySubscriber?.gender || '',
			},
		},

		// 4th row
		row4: {
			admissionDate: {
				admissionDate: eventsMeta?.admissionDate || null,
			},
			admissionHR: {
				admissionHR: eventsMeta?.admissionHour || null,
			},
			admissionType: {
				admissionType: extensionMeta?.clSection?.admissionTypeCode || '',
			},
			admissionSRC: {
				admissionSRC: extensionMeta?.clSection?.admissionSourceCode || '',
			},
			dhr: {
				dhr: extensionMeta?.dischargeTime || '',
			},
			stat: {
				stat: extensionMeta?.clSection?.patientStatusCode || '',
			},
		},

		// 5th row
		row5: {
			// condition codes
			conditionCode: {
				18: supportingInfoMeta?.conditionInfo?.[0] || '',
				19: supportingInfoMeta?.conditionInfo?.[1] || '',
				20: supportingInfoMeta?.conditionInfo?.[2] || '',
				21: supportingInfoMeta?.conditionInfo?.[3] || '',
				22: supportingInfoMeta?.conditionInfo?.[4] || '',
				23: supportingInfoMeta?.conditionInfo?.[5] || '',
				24: supportingInfoMeta?.conditionInfo?.[6] || '',
				25: supportingInfoMeta?.conditionInfo?.[7] || '',
				26: supportingInfoMeta?.conditionInfo?.[8] || '',
				27: supportingInfoMeta?.conditionInfo?.[9] || '',
				28: supportingInfoMeta?.conditionInfo?.[10] || '',
			},
			// accident state
			accidentState: {
				accidentState: claimInfo?.locationNumber?.value || '',
			},
		},

		// 6th row
		row6: {
			occurrenceDate1: {
				code1: supportingInfoMeta?.occurrenceInfo?.[0]?.code || '',
				date1: supportingInfoMeta?.occurrenceInfo?.[0]?.timingDate || null,
				code2: supportingInfoMeta?.occurrenceInfo?.[1]?.code || '',
				date2: supportingInfoMeta?.occurrenceInfo?.[1]?.timingDate || null,
			},
			occurrenceDate2: {
				code1: supportingInfoMeta?.occurrenceInfo?.[2]?.code || '',
				date1: supportingInfoMeta?.occurrenceInfo?.[2]?.timingDate || null,
				code2: supportingInfoMeta?.occurrenceInfo?.[3]?.code || '',
				date2: supportingInfoMeta?.occurrenceInfo?.[3]?.timingDate || null,
			},
			occurrenceDate3: {
				code1: supportingInfoMeta?.occurrenceInfo?.[4]?.code || '',
				date1: supportingInfoMeta?.occurrenceInfo?.[4]?.timingDate || null,
				code2: supportingInfoMeta?.occurrenceInfo?.[5]?.code || '',
				date2: supportingInfoMeta?.occurrenceInfo?.[5]?.timingDate || null,
			},
			occurrenceDate4: {
				code1: supportingInfoMeta?.occurrenceInfo?.[6]?.code || '',
				date1: supportingInfoMeta?.occurrenceInfo?.[6]?.timingDate || null,
				code2: supportingInfoMeta?.occurrenceInfo?.[7]?.code || '',
				date2: supportingInfoMeta?.occurrenceInfo?.[7]?.timingDate || null,
			},
		},

		// 7th row
		row7: {
			occurrenceSpan1: {
				code1: supportingInfoMeta?.occurrenceSpanInfo?.[0]?.code || '',
				date1: supportingInfoMeta?.occurrenceSpanInfo?.[0]?.from || null,
				through1: supportingInfoMeta?.occurrenceSpanInfo?.[0]?.through || null,
				code2: supportingInfoMeta?.occurrenceSpanInfo?.[1]?.code || '',
				date2: supportingInfoMeta?.occurrenceSpanInfo?.[1]?.from || null,
				through2: supportingInfoMeta?.occurrenceSpanInfo?.[1]?.through || null,
			},
			occurrenceSpan2: {
				code1: supportingInfoMeta?.occurrenceSpanInfo?.[2]?.code || '',
				date1: supportingInfoMeta?.occurrenceSpanInfo?.[2]?.from || null,
				through1: supportingInfoMeta?.occurrenceSpanInfo?.[2]?.through || null,
				code2: supportingInfoMeta?.occurrenceSpanInfo?.[3]?.code || '',
				date2: supportingInfoMeta?.occurrenceSpanInfo?.[3]?.from || null,
				through2: supportingInfoMeta?.occurrenceSpanInfo?.[3]?.through || null,
			},
			reasonForDelay: {
				reasonForDelay: getDisplayOrCode(extensionMeta?.clmSection?.delayReasonCode) || '',
			},
		},

		// 8th row
		row8: {
			// subscriber info
			subscriberInfo: {
				firstName: primarySubscriber?.nameMeta?.[0]?.firstName,
				lastName: primarySubscriber?.nameMeta?.[0]?.lastName,
				middleName: primarySubscriber?.nameMeta?.[0]?.middleNames?.[0],
				addressLine1: primarySubscriber?.address?.[0]?.line?.[0],
				addressLine2: primarySubscriber?.address?.[0]?.line?.[1],
				city: primarySubscriber?.address?.[0]?.city,
				state: primarySubscriber?.address?.[0]?.state,
				zip: primarySubscriber?.address?.[0]?.postalCode,
			},
			// value amount
			// card1
			valueCodes1: {
				code1: supportingInfoMeta?.valueInfo?.[0]?.code || '',
				amount1: supportingInfoMeta?.valueInfo?.[0]?.value || '',
				code2: supportingInfoMeta?.valueInfo?.[1]?.code || '',
				amount2: supportingInfoMeta?.valueInfo?.[1]?.value,
				code3: supportingInfoMeta?.valueInfo?.[2]?.code || '',
				amount3: supportingInfoMeta?.valueInfo?.[2]?.value || '',
				code4: supportingInfoMeta?.valueInfo?.[3]?.code || '',
				amount4: supportingInfoMeta?.valueInfo?.[3]?.value || '',
			},
			// card2
			valueCodes2: {
				code1: supportingInfoMeta?.valueInfo?.[4]?.code || '',
				amount1: supportingInfoMeta?.valueInfo?.[4]?.value || '',
				code2: supportingInfoMeta?.valueInfo?.[5]?.code || '',
				amount2: supportingInfoMeta?.valueInfo?.[5]?.value || '',
				code3: supportingInfoMeta?.valueInfo?.[6]?.code || '',
				amount3: supportingInfoMeta?.valueInfo?.[6]?.value || '',
				code4: supportingInfoMeta?.valueInfo?.[7]?.code || '',
				amount4: supportingInfoMeta?.valueInfo?.[7]?.value || '',
			},
			// card3
			valueCodes3: {
				code1: supportingInfoMeta?.valueInfo?.[8]?.code || '',
				amount1: supportingInfoMeta?.valueInfo?.[8]?.value || '',
				code2: supportingInfoMeta?.valueInfo?.[9]?.code || '',
				amount2: supportingInfoMeta?.valueInfo?.[9]?.value || '',
				code3: supportingInfoMeta?.valueInfo?.[10]?.code || '',
				amount3: supportingInfoMeta?.valueInfo?.[10]?.value || '',
				code4: supportingInfoMeta?.valueInfo?.[11]?.code || '',
				amount4: supportingInfoMeta?.valueInfo?.[11]?.value || '',
			},
		},

		// ninth row
		row9: {
			revenueCodeTable: {
				table: response,
			},
		},

		// tenth row
		row10: {
			payerTable: {
				table: response,
			},
			billingProvider: {
				npi: billingProvider?.npi?.value,
			},
		},

		// eleventh row
		row11: {
			insuredTable: {
				table: subscribersAndPayers,
			},
		},

		// twelfth row
		row12: {
			treatmentAuthorizationTable: {
				table: response,
			},
			diagnosisCodeQualifier: {
				code: diagnosisInfo?.diagnosisCodeQualifier,
			},
		},

		// thirteenth row
		row13: {
			// admitting physician
			primaryDiagnosis: {
				code: diagnosisInfo?.principalDx?.[0]?.code || '',
				primaryDiagnosis: diagnosisInfo?.principalDx?.[0]?.poaIndicator,
			},
			// codeA to codeQ
			otherDiagnosis: {
				codeA: diagnosisInfo?.otherDx?.[0]?.code || '',
				codeB: diagnosisInfo?.otherDx?.[1]?.code || '',
				codeC: diagnosisInfo?.otherDx?.[2]?.code || '',
				codeD: diagnosisInfo?.otherDx?.[3]?.code || '',
				codeE: diagnosisInfo?.otherDx?.[4]?.code || '',
				codeF: diagnosisInfo?.otherDx?.[5]?.code || '',
				codeG: diagnosisInfo?.otherDx?.[6]?.code || '',
				codeH: diagnosisInfo?.otherDx?.[7]?.code || '',
				codeI: diagnosisInfo?.otherDx?.[8]?.code || '',
				codeJ: diagnosisInfo?.otherDx?.[9]?.code || '',
				codeK: diagnosisInfo?.otherDx?.[10]?.code || '',
				codeL: diagnosisInfo?.otherDx?.[11]?.code || '',
				codeM: diagnosisInfo?.otherDx?.[12]?.code || '',
				codeN: diagnosisInfo?.otherDx?.[13]?.code || '',
				codeO: diagnosisInfo?.otherDx?.[14]?.code || '',
				codeP: diagnosisInfo?.otherDx?.[15]?.code || '',
				codeQ: diagnosisInfo?.otherDx?.[16]?.code || '',
				// value indicator A to Q
				valueA: diagnosisInfo?.otherDx?.[0]?.poaIndicator,
				valueB: diagnosisInfo?.otherDx?.[1]?.poaIndicator,
				valueC: diagnosisInfo?.otherDx?.[2]?.poaIndicator,
				valueD: diagnosisInfo?.otherDx?.[3]?.poaIndicator,
				valueE: diagnosisInfo?.otherDx?.[4]?.poaIndicator,
				valueF: diagnosisInfo?.otherDx?.[5]?.poaIndicator,
				valueG: diagnosisInfo?.otherDx?.[6]?.poaIndicator,
				valueH: diagnosisInfo?.otherDx?.[7]?.poaIndicator,
				valueI: diagnosisInfo?.otherDx?.[8]?.poaIndicator,
				valueJ: diagnosisInfo?.otherDx?.[9]?.poaIndicator,
				valueK: diagnosisInfo?.otherDx?.[10]?.poaIndicator,
				valueL: diagnosisInfo?.otherDx?.[11]?.poaIndicator,
				valueM: diagnosisInfo?.otherDx?.[12]?.poaIndicator,
				valueN: diagnosisInfo?.otherDx?.[13]?.poaIndicator,
				valueO: diagnosisInfo?.otherDx?.[14]?.poaIndicator,
				valueP: diagnosisInfo?.otherDx?.[15]?.poaIndicator,
				valueQ: diagnosisInfo?.otherDx?.[16]?.poaIndicator,
			},
		},

		// fourteenth row
		row14: {
			admitDx: {
				admitDx: diagnosisInfo?.admitDx?.[0]?.code || '',
			},
			patientReasonDx: {
				patientReasonDx1: supportingInfoMeta?.patientReasonOfVisit?.[0]?.code || '',
				patientReasonDx2: supportingInfoMeta?.patientReasonOfVisit?.[1]?.code || '',
				patientReasonDx3: supportingInfoMeta?.patientReasonOfVisit?.[2]?.code || '',
			},
			ppsCode: {
				ppsCode: response?.diagnosisRelatedGroupInfo || '',
			},
			eci: {
				eci1: supportingInfoMeta?.externalCauseOfInjury?.[0]?.code || '',
				eci1Indicator: supportingInfoMeta?.externalCauseOfInjury?.[0]?.indicator || '',
				eci2: supportingInfoMeta?.externalCauseOfInjury?.[1]?.code || '',
				eci2Indicator: supportingInfoMeta?.externalCauseOfInjury?.[1]?.indicator || '',
				eci3: supportingInfoMeta?.externalCauseOfInjury?.[2]?.code || '',
				eci3Indicator: supportingInfoMeta?.externalCauseOfInjury?.[2]?.indicator || '',
			},
		},

		// fifteenth row
		row15: {
			principleProcedure: {
				code: procedureInfo?.primaryProcedure?.[0]?.code || '',
				date: procedureInfo?.primaryProcedure?.[0]?.date || null,
			},
			otherProcedure1: {
				code: procedureInfo?.otherProcedure?.[0]?.code || '',
				date: procedureInfo?.otherProcedure?.[0]?.date || null,
			},
			otherProcedure2: {
				code: procedureInfo?.otherProcedure?.[1]?.code || '',
				date: procedureInfo?.otherProcedure?.[1]?.date || null,
			},
		},

		// sixteenth row
		row16: {
			otherProcedure3: {
				code: procedureInfo?.otherProcedure?.[2]?.code || '',
				date: procedureInfo?.otherProcedure?.[2]?.date || null,
			},
			otherProcedure4: {
				code: procedureInfo?.otherProcedure?.[3]?.code || '',
				date: procedureInfo?.otherProcedure?.[3]?.date || null,
			},
			otherProcedure5: {
				code: procedureInfo?.otherProcedure?.[4]?.code || '',
				date: procedureInfo?.otherProcedure?.[4]?.date || null,
			},
		},

		// seventeenth row
		row17: {
			// attending physician
			attendingPhysician: {
				npi: attendingPhysician?.npi?.value || '',
				qual: attendingPhysicianSecondaryId?.code || '',
				qualValue: attendingPhysicianSecondaryId?.value || '',
				lastName: attendingPhysician?.nameMeta?.[0]?.lastName || '',
				firstName: attendingPhysician?.nameMeta?.[0]?.firstName || '',
				middleName: attendingPhysician?.nameMeta?.[0]?.middleNames?.[0] || '',
				reference: attendingPhysician?.reference,
			},
			// operating physician
			operatingPhysician: {
				npi: operatingPhysician?.npi?.value || '',
				qual: operatingPhysicianSecondaryId?.code || '',
				qualValue: operatingPhysicianSecondaryId?.value || '',
				lastName: operatingPhysician?.nameMeta?.[0]?.lastName || '',
				firstName: operatingPhysician?.nameMeta?.[0]?.firstName || '',
				middleName: operatingPhysician?.nameMeta?.[0]?.middleNames?.[0] || '',
				reference: operatingPhysician?.reference,
			},
		},

		// eighteen row
		row18: {
			// other operating physician1
			otherPhysician1: {
				type: otherPhysician1TypeCode ?? otherPhysician1?.type ?? '',
				npi: otherPhysician1?.npi?.value || '',
				qual: otherPhysician1SecondaryId?.code || '',
				qualValue: otherPhysician1SecondaryId?.value || '',
				lastName: otherPhysician1?.nameMeta?.[0]?.lastName || '',
				firstName: otherPhysician1?.nameMeta?.[0]?.firstName || '',
				middleName: otherPhysician1?.nameMeta?.[0]?.middleNames?.[0] || '',
				reference: otherPhysician1?.reference,
			},
			// other operating physician2
			otherPhysician2: {
				type: otherPhysician2TypeCode ?? otherPhysician2?.type ?? '',
				npi: otherPhysician2?.npi?.value || '',
				qual: otherPhysician2SecondaryId?.code || '',
				qualValue: otherPhysician2SecondaryId?.value || '',
				lastName: otherPhysician2?.nameMeta?.[0]?.lastName || '',
				firstName: otherPhysician2?.nameMeta?.[0]?.firstName || '',
				middleName: otherPhysician2?.nameMeta?.[0]?.middleNames?.[0] || '',
				reference: otherPhysician2?.reference,
			},
		},

		// nineteenth row
		row19: {
			remarks: {
				type1: extensionMeta?.note?.[0]?.noteCode || '',
				type2: extensionMeta?.note?.[1]?.noteCode || '',
				type3: extensionMeta?.note?.[2]?.noteCode || '',
				remark1: extensionMeta?.note?.[0]?.noteText || '',
				remark2: extensionMeta?.note?.[1]?.noteText || '',
				remark3: extensionMeta?.note?.[2]?.noteText || '',
			},
		},

		// twentieth row
		row20: {
			qualTable: {
				table: response,
			},
		},

		// twenty first row
		...(isSecondaryClaim && {
			row21: {
				payerInformation: {
					payerName: payer?.name,
				},
				// todo: add after BE confirms mapping for this field
				claimFilingIndicator: {
					claimFilingIndicator: '',
				},
				primaryPayerId: {
					primaryPayerId: payer?.payorIdentification?.value,
				},
				insuranceTypeCode: {
					insuranceTypeCode: getDisplayOrCode(subscribersAndPayers?.[0]?.coverage?.insuranceTypeCoding),
				},
				additionalLineItemTable: {
					table: response,
				},
				claimAdjudicationTable: {
					table: response,
				},
			},
		}),

		// additional information
		additionalRow: {
			reportInfo: {
				reportTypeCode: supportingInfoMeta?.pwkMeta?.[0]?.attachmentReportTypeCode?.code || '',
				reportTransmissionCode: supportingInfoMeta?.pwkMeta?.[0]?.attachmentReportTransmissionCode?.code || '',
				attachmentControlNumber: supportingInfoMeta?.pwkMeta?.[0]?.attachmentControlNumber || '',
			},
			// facility
			facility: {
				facilityName: serviceFacility?.name,
				address1: serviceFacility?.address?.line?.[0] || serviceFacility?.contact?.[0]?.address?.line?.[0],
				address2: serviceFacility?.address?.line?.[1] || serviceFacility?.contact?.[0]?.address?.line?.[1],
				state: serviceFacility?.address?.state || serviceFacility?.contact?.[0]?.address?.state,
				city: serviceFacility?.address?.city || serviceFacility?.contact?.[0]?.address?.city,
				zip: serviceFacility?.address?.postalCode || serviceFacility?.contact?.[0]?.address?.postalCode,
				npi: serviceFacility?.npi?.value,
				stateLicenseNumber: serviceFacility?.stateLicenseNumber?.value,
				commId: serviceFacility?.providerCommercialNumber?.value,
				locationId: serviceFacility?.locationNumber?.value,
			},
		},
	};
};

const getProfessionalClaimInitialModel = (response: ProfessionalClaimParser.IResponse): ProfessionalForm.FormModel => {
	const {
		billingProvider,
		claimInfo,
		eventsMeta,
		patient,
		payer,
		careTeamMeta,
		diagnosisInfoMeta,
		subscribersAndPayers,
		extensionMeta,
		serviceFacility,
	} = response;
	// primary and other subscriber, payer and coverage
	const primarySubscriber = subscribersAndPayers?.[0]?.subscriber;
	const primaryCoverage = subscribersAndPayers?.[0]?.coverage;
	const otherSubscriber = subscribersAndPayers?.[1]?.subscriber;
	const otherCoverage = subscribersAndPayers?.[1]?.coverage;
	const otherPayer = subscribersAndPayers?.[1]?.payer;

	const currentIllnessDateMeta = eventsMeta?.admissionDateMeta ?? eventsMeta?.onsetOfInjuryMeta;
	const otherDateMeta = eventsMeta?.firstConsultationDateMeta ?? eventsMeta?.onsetOfInjuryMeta;
	const payToGroupMeta = getPayToGroupMeta(extensionMeta?.payToAddress);
	const supervisingPhysician = getFirstCareTeamMeta(careTeamMeta?.supervisingPhysician);

	// ordering provider
	const orderingProvider = getFirstCareTeamMeta(careTeamMeta?.orderingProvider);
	const firstOrderingProviderSequence = orderingProvider?.sequence;
	const lineItemContainsOrderingProvider = _.find(response?.lineItems, (item) =>
		_.includes(item?.careTeamSequence, firstOrderingProviderSequence),
	);

	const outsideLabExpanse = _.sumBy(response?.lineItems, (item) => item?.outsideLabExpanse);

	const otherProvider = getFirstCareTeamMeta(
		careTeamMeta?.referringProvider || careTeamMeta?.primaryCareProvider || careTeamMeta?.renderingProvider,
	);
	const otherProviderSecondaryId = getSecondaryId(otherProvider, true);
	const isAssignmentAccepted = _.includes(
		[PLAN_ASSIGNMENT_CODES.A, PLAN_ASSIGNMENT_CODES.B],
		extensionMeta?.clmSection?.planAssignmentCode?.code,
	);
	const assignmentNotAccepted = extensionMeta?.clmSection?.planAssignmentCode?.code === PLAN_ASSIGNMENT_CODES.C;
	const isSecondaryClaim = claimInfo?.payerResponsibilitySequence === payerResponsibilitySequenceMapping?.[2]?.order;

	const taxIdType = getTaxIdType(billingProvider?.ein?.value, billingProvider?.ssn?.value);
	const acceptAssignmentCode = getAcceptAssignmentCode(isAssignmentAccepted, assignmentNotAccepted);

	return {
		// first row
		row1: {
			payer: {
				payerName: payer?.name,
				payerId: payer?.payorIdentification?.value,
				payerType: getDisplayOrCode(subscribersAndPayers?.[0]?.coverage?.payerTypeCoding),
				addressLine1: payer?.contact?.[0]?.address?.line?.[0],
				addressLine2: payer?.contact?.[0]?.address?.line?.[1],
				city: payer?.contact?.[0]?.address?.city,
				state: payer?.contact?.[0]?.address?.state,
				zip: payer?.contact?.[0]?.address?.postalCode,
			},
			insuredMemberId: {
				memberId: primarySubscriber?.memberId?.value,
			},
		},

		// second row
		row2: {
			patientName: {
				lastName: patient?.nameMeta?.[0]?.lastName || primarySubscriber?.nameMeta?.[0]?.lastName,
				firstName: patient?.nameMeta?.[0]?.firstName || primarySubscriber?.nameMeta?.[0]?.firstName,
				middleName: patient?.nameMeta?.[0]?.middleNames?.[0] || primarySubscriber?.nameMeta?.[0]?.middleNames?.[0],
			},
			patientInfo: {
				dateOfBirth: patient?.birthDate || primarySubscriber?.birthDate,
				sex: patient?.gender || primarySubscriber?.gender,
			},
			insuredName: {
				lastName: primarySubscriber?.nameMeta?.[0]?.lastName,
				firstName: primarySubscriber?.nameMeta?.[0]?.firstName,
				middleName: primarySubscriber?.nameMeta?.[0]?.middleNames?.[0],
			},
		},

		// third row
		row3: {
			patientAddress: {
				addressLine1: patient?.address?.[0]?.line?.[0] || primarySubscriber?.address?.[0]?.line?.[0],
				addressLine2: patient?.address?.[0]?.line?.[1] || primarySubscriber?.address?.[0]?.line?.[1],
				city: patient?.address?.[0]?.city || primarySubscriber?.address?.[0]?.city,
				state: patient?.address?.[0]?.state || primarySubscriber?.address?.[0]?.state,
				zip: patient?.address?.[0]?.postalCode || primarySubscriber?.address?.[0]?.postalCode,
				telephone: primarySubscriber?.contact?.[0]?.telecom?.[0]?.value,
			},
			patientRelationship: {
				relationship: primarySubscriber?.patientRelation,
			},
			insuredAddress: {
				addressLine1: primarySubscriber?.address?.[0]?.line?.[0],
				addressLine2: primarySubscriber?.address?.[0]?.line?.[1],
				city: primarySubscriber?.address?.[0]?.city,
				state: primarySubscriber?.address?.[0]?.state,
				zip: primarySubscriber?.address?.[0]?.postalCode,
				telephone: primarySubscriber?.contact?.[0]?.telecom?.[0]?.value,
			},
		},

		// fourth row
		row4: {
			otherInsured: {
				lastName: otherSubscriber?.nameMeta?.[0]?.lastName,
				firstName: otherSubscriber?.nameMeta?.[0]?.firstName,
				mi: otherSubscriber?.nameMeta?.[0]?.middleNames?.[0],
				relationship: otherSubscriber?.patientRelation,
				policyId: otherCoverage?.planCode,
				insuredId: otherSubscriber?.memberId?.value,
				indicatorCode: getDisplayOrCode(response?.subscribersAndPayers?.[0]?.coverage?.payerTypeCoding),
				planName: otherCoverage?.planName,
				insuranceTypeCode: getDisplayOrCode(otherCoverage?.insuranceTypeCoding),
				resSequence: otherCoverage?.payerResponsibility,
				payerId: otherPayer?.payorIdentification?.value,
			},
			patientCondition: {
				employment:
					response?.extensionMeta?.clmSection?.relatedCauseCode?.code === CLAIM_RELATED_CAUSES_CODES.EM
						? YES_NO_CODES.Y
						: YES_NO_CODES.N,
				autoAccident:
					response?.extensionMeta?.clmSection?.relatedCauseCode?.code === CLAIM_RELATED_CAUSES_CODES.AA
						? YES_NO_CODES.Y
						: YES_NO_CODES.N,
				autoAccidentState: response?.extensionMeta?.clmSection?.autoAccidentStateCode?.code,
				otherAccident:
					response?.extensionMeta?.clmSection?.relatedCauseCode?.code === CLAIM_RELATED_CAUSES_CODES.OA
						? YES_NO_CODES.Y
						: YES_NO_CODES.N,
				claimCodes: '', // this will be empty as it is NUCC specific
			},
			insuredPolicy: {
				policyNumber: primaryCoverage?.planCode,
				dateOfBirth: primarySubscriber?.birthDate,
				sex: primarySubscriber?.gender,
				otherClaimId: primarySubscriber?.agencyClaimNumber?.value || patient?.agencyClaimNumber?.value,
				planName: primaryCoverage?.planName,
				anotherHealthPlan: _.isEmpty(otherCoverage) ? YES_NO_CODES.N : YES_NO_CODES.Y,
			},
		},

		// fifth row
		row5: {
			patientSignature: {
				electronicSignature:
					response?.extensionMeta?.clmSection?.releaseOfInformationCode?.code === YES_NO_CODES.Y
						? YES_NO_CODES.Y
						: YES_NO_CODES.N,
				date: '', // todo check letter (currently not available in x12)
			},
			insuredSignature: {
				signatureOnFile: response?.extensionMeta?.clmSection?.benefitAssignmentIndicator?.code === YES_NO_CODES.Y,
			},
		},

		// sixth row
		row6: {
			currentIllness: {
				date: currentIllnessDateMeta?.date,
				qualifier: currentIllnessDateMeta?.display,
			},
			otherDate: {
				date: otherDateMeta?.date,
				qualifier: otherDateMeta?.display,
			},
			datesUnableToWork: {
				from: response?.eventsMeta?.initialDisabilityDateMeta?.date || response?.eventsMeta?.disabilityDatePeriodMeta?.startDate,
				to: response?.eventsMeta?.initialDisabilityEndDateMeta?.date || response?.eventsMeta?.disabilityDatePeriodMeta?.endDate,
			},
		},

		// seventh row
		row7: {
			provider: {
				firstName: otherProvider?.nameMeta?.[0]?.firstName,
				lastName: otherProvider?.nameMeta?.[0]?.lastName,
				provider: otherProvider?.type,
				mi: otherProvider?.nameMeta?.[0]?.middleNames?.[0],
				reference: otherProvider?.reference,
			},
			secondaryId: {
				secondaryIdType: otherProviderSecondaryId?.code,
				secondaryId: otherProviderSecondaryId?.value,
			},
			npi: {
				npi: otherProvider?.npi?.value,
			},
			hospitalizationDates: {
				from: response?.eventsMeta?.admissionDateMeta?.date,
				to: response?.eventsMeta?.dischargeDateMeta?.date,
			},
		},

		// eighth row
		row8: {
			additionalClaimInfo: {
				additionalInfo: '', // designated by NUCC so additional info should not fill
				additionalInfoCode: '',
			},
			supplementalClaimInfo: {
				tryCode: response?.supportingInfoMeta?.pwkMeta?.[0]?.attachmentReportTypeCode?.code,
				transCode: response?.supportingInfoMeta?.pwkMeta?.[0]?.attachmentReportTransmissionCode?.code,
				attachmentId: response?.supportingInfoMeta?.pwkMeta?.[0]?.attachmentControlNumber,
			},
			outsideLab: {
				isOutsideLab: outsideLabExpanse > 0 ? YES_NO_CODES.Y : YES_NO_CODES.N,
				charge: formatCurrency(outsideLabExpanse),
			},
		},

		// ninth row
		row9: {
			diagnosisCodes: {
				A: diagnosisInfoMeta?.[0]?.code,
				B: diagnosisInfoMeta?.[1]?.code,
				C: diagnosisInfoMeta?.[2]?.code,
				D: diagnosisInfoMeta?.[3]?.code,
				E: diagnosisInfoMeta?.[4]?.code,
				F: diagnosisInfoMeta?.[5]?.code,
				G: diagnosisInfoMeta?.[6]?.code,
				H: diagnosisInfoMeta?.[7]?.code,
				I: diagnosisInfoMeta?.[8]?.code,
				J: diagnosisInfoMeta?.[9]?.code,
				K: diagnosisInfoMeta?.[10]?.code,
				L: diagnosisInfoMeta?.[11]?.code,
			},
			resubmission: {
				code: extensionMeta?.clmSection?.claimFrequencyCode,
				refNo: claimInfo?.originalReferenceNumber?.value,
			},
			priorAuthorization: {
				number: claimInfo?.priorAuthorizationNumber?.value,
			},
		},

		// tenth row
		row10: {
			serviceLineInfo: {
				info: response,
			},
		},

		// eleventh row
		row11: {
			federalTaxId: {
				number: billingProvider?.ein?.value || billingProvider?.ssn?.value,
				type: taxIdType,
			},
			accountNumber: {
				number: claimInfo?.patientControlNumber,
			},
			acceptAssignment: {
				isAccepted: acceptAssignmentCode,
			},
			totalCharge: {
				charge: formatCurrency(claimInfo?.total?.value),
			},
			amountPaid: {
				amount: formatCurrency(claimInfo?.patientPaid?.value),
			},
			rsvdForNuccUse: {
				amount: '', // for NUCC use only
			},
		},

		// twelfth row
		row12: {
			signatureDetails: {
				signature: extensionMeta?.clmSection?.providerSignIndicator?.code,
				date: '', // todo check letter (currently not available in x12)
			},
			serviceFacility: {
				facilityName: serviceFacility?.name,
				addressLine1: serviceFacility?.address?.line?.[0] || serviceFacility?.contact?.[0]?.address?.line?.[0],
				addressLine2: serviceFacility?.address?.line?.[1] || serviceFacility?.contact?.[0]?.address?.line?.[1],
				city: serviceFacility?.address?.city || serviceFacility?.contact?.[0]?.address?.city,
				state: serviceFacility?.address?.state || serviceFacility?.contact?.[0]?.address?.state,
				zip: serviceFacility?.address?.postalCode || serviceFacility?.contact?.[0]?.address?.postalCode,
			},
			billingProvider: {
				phone: getTelephone(billingProvider?.contact),
				lastName: billingProvider?.nameMeta?.[0]?.lastName || billingProvider?.name,
				firstName: billingProvider?.nameMeta?.[0]?.firstName,
				middleInitial: billingProvider?.nameMeta?.[0]?.middleNames?.[0],
				addressLine1: billingProvider?.address?.[0]?.line?.[0] || billingProvider?.contact?.[0]?.address?.line?.[0],
				addressLine2: billingProvider?.address?.[0]?.line?.[1] || billingProvider?.contact?.[0]?.address?.line?.[1],
				city: billingProvider?.address?.[0]?.city || billingProvider?.contact?.[0]?.address?.city,
				state: billingProvider?.address?.[0]?.state || billingProvider?.contact?.[0]?.address?.state,
				zip: billingProvider?.address?.[0]?.postalCode || billingProvider?.contact?.[0]?.address?.postalCode,
				npi: billingProvider?.npi?.value,
				taxonomyCode: billingProvider?.taxonomyCode,
			},
		},

		// thirteenth row for secondary claim
		...(isSecondaryClaim && {
			row13: {
				payerInformation: {
					payerName: payer?.name,
				},
				// todo: add after BE confirms mapping for this field
				claimFilingIndicator: {
					claimFilingIndicator: '',
				},
				primaryPayerId: {
					primaryPayerId: payer?.payorIdentification?.value,
				},
				insuranceTypeCode: {
					insuranceTypeCode: getDisplayOrCode(subscribersAndPayers?.[0]?.coverage?.insuranceTypeCoding),
				},
				additionalLineItemTable: {
					table: response,
				},
				claimAdjudicationTable: {
					table: response,
				},
			},
		}),

		// additional fields
		additionalFields: {
			dates: {
				initialTreatment: eventsMeta?.initialTreatmentDateMeta?.date,
				latestVisit: eventsMeta?.lastVisitDateMeta?.date,
			},
			supervisingPhysician: {
				firstName: supervisingPhysician?.nameMeta?.[0]?.firstName,
				lastName: supervisingPhysician?.nameMeta?.[0]?.lastName,
				middleInitial: supervisingPhysician?.nameMeta?.[0]?.middleNames?.[0],
				npi: supervisingPhysician?.npi?.value,
				qual: getSecondaryId(supervisingPhysician, true)?.code,
				id: getSecondaryId(supervisingPhysician, true)?.value,
			},
			orderingPhysician: {
				firstName: orderingProvider?.nameMeta?.[0]?.firstName,
				lastName: orderingProvider?.nameMeta?.[0]?.lastName,
				middleInitial: orderingProvider?.nameMeta?.[0]?.middleNames?.[0],
				npi: orderingProvider?.npi?.value,
				qual: getSecondaryId(orderingProvider, true)?.code,
				id: getSecondaryId(orderingProvider, true)?.value,
				clia: lineItemContainsOrderingProvider?.cliaNumber?.value,
				accidentDate: eventsMeta?.accidentDateMeta?.date,
				mammographyCertificate: lineItemContainsOrderingProvider?.mammographyCertificationNumber?.value,
			},
			ambulance: {
				transportCode: '', // todo check ambulance transport code, its not in x-12
				miles: getQuantity(extensionMeta?.cr1Section?.transportDistance),
				transportReasonCode: getDisplayOrCode(extensionMeta?.cr1Section?.ambulanceTransportReasonCode),
			},
			attachment: {
				reportTypeCode: response?.supportingInfoMeta?.pwkMeta?.[1]?.attachmentReportTypeCode?.code,
				reportTransmissionCode: response?.supportingInfoMeta?.pwkMeta?.[1]?.attachmentReportTransmissionCode?.code,
				attachmentControlNumber: response?.supportingInfoMeta?.pwkMeta?.[1]?.attachmentControlNumber,
			},
			hospiceEmployee: {
				hospiceEmployee: getDisplayOrCode(extensionMeta?.crc70Section?.hospiceIndicator),
			},
			referralNumber: {
				referralNumber: claimInfo?.referralNumber?.value,
			},
			contract: {
				contractTypeCode: getDisplayOrCode(extensionMeta?.cn1Section?.contractType),
				amount: formatCurrency(extensionMeta?.cn1Section?.contractAmount?.value),
				percentage: getQuantity(extensionMeta?.cn1Section?.contractQuantity),
				code: getDisplayOrCode(extensionMeta?.cn1Section?.contractCode),
				termDiscountPercent: getQuantity(extensionMeta?.cn1Section?.termsDiscountPercentage),
				contactVersionIdentifier: response?.extensionMeta?.cn1Section?.contractVersionIdentifier,
			},
			payTo: {
				name: '', // for pay to address, we never gonna get name, npi, taxId and id
				npi: '',
				taxId: '',
				id: '',
				addressLine1: payToGroupMeta?.addressLine1,
				addressLine2: payToGroupMeta?.addressLine2,
				city: payToGroupMeta?.city,
				state: payToGroupMeta?.state,
				zip: payToGroupMeta?.zip,
			},
		},
	};
};

export {
	getInstitutionalClaimInitialModel,
	getProfessionalClaimInitialModel,
	getDisplayOrCode,
	getSecondaryId,
	formatSecondaryId,
	getQuantity,
};
